import * as React from "react";
import { useTheme } from "react-jss";
import { useSpring, animated, to } from "react-spring";
import { useDrag } from "@use-gesture/react";
import classnames from "classnames";
import { uniq, clamp } from "../../utils/lodash";
import useStyles from "./CardSortControl.styles";
import { useLanguage } from "../../hooks/useLanguage";
import { ControlProps } from "./BaseControl";
import { translate } from "../../models/utilities/translationUtilities";
import { useElementDimensions } from "../../hooks/useElementDimensions";
import useRecall from "../../hooks/useRecall";
import { VariableModel } from "../../models/VariableModel";
import { usePickMentions, useSetVariableMentions } from "../../hooks/useMentions";
import { usePrevious } from "../../hooks/usePrevious";
import { ChoiceModel } from "../../models/ChoiceModel";
import { useValidation, useDiscreteVariadicValidator } from "../../hooks/useValidation";
import { getSettingString } from "../../models/utilities/settingUtilities";
import { getChoice } from "../../models/utilities/variableUtilities";
import { shouldUseModernTheme } from "../../theme/navigationButtonThemeColors";
import * as CardSortTokens from "../../theme/cardSortThemeTokens";
import {
    columnIndexAt,
    columnLeft,
    choiceIndexAtMobile,
    visualColumnIndex,
    type ChoiceBucketLayout,
} from "./cardSortLayoutUtilities";
import { MOBILE_WIDTH } from "../../consts";
import type { Theme } from "../../types/Theme";
import { stripHtmlForAccessibleName } from "../../utils/WCAGHelper";
import useTextDirection from "../../hooks/useTextDirection";

const CARD_SIZE_RATIO = 3 / 5;
const PADDING_RATIO = 2 / 100;
const MARGIN_RATIO = 2 / 100;
const MAX_CARD_SIZE = 200;
const MIN_FONT_SIZE = 12;
const MAX_FONT_SIZE = 19;
const FONT_SIZE_RATIO = 2 / 100;
const BOUNDS_MARGIN = 90;
const CARD_MAX_SPACING_RATIO = 3 / 100;

interface CardProps {
    text: string;
    color: string;
    imageUrl: string;
    width: number;
    height: number;
    padding: number;
    left: number;
    top: number;
    onDrop(xy: number[]);
    onHover?(xy: number[] | null): void;
    bounds: { top: number; bottom: number; left: number; right: number };
    id: string;
    areaCode: any;
    modern?: boolean;
    dragLiftZ?: number;
    dragShadowColor?: string;
    cardKey?: string;
    onTextHeight?: (cardKey: string, height: number) => void;
}

function CardSortCard({
    left,
    top,
    text,
    color,
    imageUrl,
    width,
    height,
    padding,
    bounds,
    onDrop,
    onHover,
    id,
    areaCode,
    modern,
    dragLiftZ = 10,
    dragShadowColor = "rgba(0,0,0,0.2)",
    cardKey,
    onTextHeight,
}: CardProps) {
    const recalledImage = useRecall(imageUrl);
    const recalledText = useRecall(text);
    const accessibleName = stripHtmlForAccessibleName(recalledText);
    const hasImage = Boolean(imageUrl);
    const displayImage = hasImage ? recalledImage || imageUrl : undefined;
    const prevBounds = usePrevious(bounds);
    const theme = useTheme<Theme>();
    const classes = useStyles({ theme, color });
    const boundsChanged = prevBounds !== bounds;
    const cardRef = React.useRef<HTMLDivElement>(null);
    const contentRef = React.useRef<HTMLDivElement>(null);

    const dragStartPos = React.useRef<[number, number] | null>(null);
    // Render drag position from state — spring updates are async and can fight gesture moves.
    const [liveDrag, setLiveDrag] = React.useState<[number, number] | null>(null);

    const [spring, controller] = useSpring(
        () => ({
            x: left,
            y: top,
            z: 0,
            immediate: (name: string) => boundsChanged && ["x", "y"].includes(name),
            config: { mass: 1, tension: 170, friction: 25 },
        }),
        [left, top, boundsChanged],
    );

    React.useLayoutEffect(() => {
        if (dragStartPos.current === null) {
            controller.set({
                x: left,
                y: top,
                z: spring.z.get(),
            });
        }
    }, [left, top, controller, spring.z]);

    React.useLayoutEffect(() => {
        if (!modern || hasImage || !onTextHeight || !cardKey) {
            return;
        }

        const cardEl = cardRef.current;
        const contentEl = contentRef.current;
        if (!cardEl || !contentEl) {
            return;
        }

        const reportHeight = () => {
            // Measure content naturally (not the fixed card height) so wrap can grow layout.
            const borderY = cardEl.offsetHeight - cardEl.clientHeight;
            const measured = contentEl.offsetHeight + borderY;
            if (measured > 0) {
                onTextHeight(cardKey, measured);
            }
        };
        reportHeight();

        if (typeof ResizeObserver === "undefined") {
            return;
        }

        const observer = new ResizeObserver(reportHeight);
        observer.observe(contentEl);
        return () => observer.disconnect();
    }, [modern, hasImage, onTextHeight, cardKey, text, width, recalledText]);

    const bind = useDrag(
        (state) => {
            const { movement, down, tap } = state;

            if (down && dragStartPos.current === null) {
                dragStartPos.current = [spring.x.get(), spring.y.get()];
            }

            const absoluteX = dragStartPos.current ? dragStartPos.current[0] + movement[0] : left;
            const absoluteY = dragStartPos.current ? dragStartPos.current[1] + movement[1] : top;

            const update = {
                x: down ? absoluteX : left,
                y: down ? absoluteY : top,
                z: down ? dragLiftZ : 0,
            };
            if (down) {
                setLiveDrag([absoluteX, absoluteY]);
                (controller as { start: (o: object) => void }).start({
                    ...update,
                    immediate: true,
                });
            } else {
                setLiveDrag(null);
                controller.set(update);
            }

            if (tap) {
                onHover?.(null);
                return;
            }

            if (down) {
                onHover?.([absoluteX, absoluteY]);
            } else {
                onHover?.(null);
                if (onDrop) {
                    onDrop([absoluteX, absoluteY]);
                }
                dragStartPos.current = null;
            }
        },
        {
            from: () => [spring.x.get(), spring.y.get()],
            capture: true,
            filterTaps: true,
            bounds: {
                ...bounds,
                right: bounds.right - width,
                bottom: bounds.bottom - height,
            },
        },
    );

    const shadowAlpha = React.useMemo(() => {
        const match = dragShadowColor.match(/[\d.]+\)$/);
        return match ? parseFloat(match[0]) : 0.2;
    }, [dragShadowColor]);

    const dragScale = 1 + dragLiftZ * 0.01;

    return (
        <animated.div
            ref={cardRef}
            style={{
                // Anchor to container top-left so translate() is dir-independent under dir=rtl.
                left: 0,
                top: 0,
                width,
                height,
                transform:
                    liveDrag !== null
                        ? `translate(${liveDrag[0]}px, ${liveDrag[1]}px) scale(${dragScale})`
                        : to(
                              [spring.x, spring.y, spring.z],
                              (x, y, z) => `translate(${x}px, ${y}px) scale(${z * 0.01 + 1})`,
                          ),
                boxShadow:
                    liveDrag !== null
                        ? `0 ${dragLiftZ * 0.3}px ${dragLiftZ + 3}px rgba(0,0,0,${shadowAlpha})`
                        : spring.z.to((z) =>
                              z <= 0
                                  ? undefined
                                  : `0 ${z * 0.3}px ${z + 3}px rgba(0,0,0,${shadowAlpha})`,
                          ),
                zIndex:
                    liveDrag !== null
                        ? dragLiftZ + 1
                        : spring.z.to((z) => Math.max(1, 1 + Math.round(z))),
                touchAction: "none",
            }}
            {...bind()}
            className={classnames(
                modern ? classes.cardModern : classes.card,
                hasImage && (modern ? classes.cardImageModern : classes.cardImageLegacy),
                modern &&
                    liveDrag !== null &&
                    (hasImage ? classes.cardDraggingImage : classes.cardDragging),
            )}
            id={id}
            data-areacode={areaCode}
            aria-label={accessibleName || undefined}
        >
            {displayImage ? (
                <img
                    src={displayImage}
                    alt=""
                    className={classes.cardImage}
                    draggable={false}
                    width={width}
                    height={height}
                />
            ) : (
                <div
                    ref={contentRef}
                    className={classnames(
                        "allowFocusOnQuestion",
                        modern && classes.cardContentModern,
                    )}
                    style={modern ? undefined : { padding }}
                    dangerouslySetInnerHTML={{ __html: recalledText }}
                />
            )}
        </animated.div>
    );
}

interface BucketProps {
    text: string;
    color: string;
    width: number;
    height: number;
    lineHeight: number;
    left: number;
    top: number;
    id: string;
    modern?: boolean;
    showDropHere?: boolean;
    dropHereStyle?: React.CSSProperties;
    titleKey?: string;
    onTitleHeight?: (titleKey: string, height: number) => void;
}

function CardSortBucket({
    text,
    color,
    width,
    height,
    lineHeight,
    left,
    top,
    id,
    modern,
    showDropHere,
    dropHereStyle,
    titleKey,
    onTitleHeight,
}: BucketProps) {
    const theme = useTheme<Theme>();
    const classes = useStyles({ theme, color });
    const titleRef = React.useRef<HTMLDivElement>(null);

    React.useLayoutEffect(() => {
        if (!modern || !onTitleHeight || !titleKey) {
            return;
        }

        const titleEl = titleRef.current;
        if (!titleEl) {
            return;
        }

        const reportHeight = () => onTitleHeight(titleKey, titleEl.offsetHeight);
        reportHeight();

        if (typeof ResizeObserver === "undefined") {
            return;
        }

        const observer = new ResizeObserver(reportHeight);
        observer.observe(titleEl);
        return () => observer.disconnect();
    }, [modern, onTitleHeight, titleKey, text, width, lineHeight]);

    if (modern) {
        return (
            <div
                className={classes.bucketModern}
                style={{ width, height, left, top }}
                id={id}
                aria-label={text}
            >
                <div
                    ref={titleRef}
                    className={classes.bucketTitleModern}
                    style={{ lineHeight: `${lineHeight}px` }}
                    dangerouslySetInnerHTML={{ __html: text }}
                />
                {showDropHere && dropHereStyle && (
                    <div className={classes.dropHereModern} style={dropHereStyle} aria-hidden />
                )}
            </div>
        );
    }

    return (
        <div
            className={classes.bucket}
            style={{
                width,
                height,
                lineHeight: `${lineHeight}px`,
                left,
                top,
            }}
            dangerouslySetInnerHTML={{ __html: text }}
            id={id}
            aria-label={text}
        />
    );
}

interface GroupProps {
    text: string;
    width: number;
    height: number;
    lineHeight: number;
    left: number;
    top: number;
}

function CardSortGroup({ text, width, height, lineHeight, left, top }: GroupProps) {
    const theme = useTheme<Theme>();
    const classes = useStyles({ theme });

    return (
        <div
            className={classes.group}
            style={{
                width,
                height,
                lineHeight: `${lineHeight}px`,
                left,
                top,
            }}
            dangerouslySetInnerHTML={{ __html: text }}
            aria-label={text}
        />
    );
}

interface GroupHeaderProps {
    text: string;
    width: number;
    lineHeight: number;
    left: number;
    top: number;
}

function CardSortGroupHeader({ text, width, lineHeight, left, top }: GroupHeaderProps) {
    const theme = useTheme<Theme>();
    const classes = useStyles({ theme });

    if (!text) {
        return null;
    }

    return (
        <div
            className={classes.groupHeaderModern}
            style={{ width, height: lineHeight, left, top }}
            aria-label={text}
        >
            <div
                className={classes.groupLabelModern}
                style={{ lineHeight: `${lineHeight}px` }}
                dangerouslySetInnerHTML={{ __html: text }}
            />
            <div className={classes.groupDividerModern} />
        </div>
    );
}

export function CardSortControl(props: ControlProps) {
    const { question, sequencedVariables, validateSignal } = props;
    const [language] = useLanguage();
    const [insertionOrder, setInsertionOrder] = React.useState<number[]>([]);
    const theme = useTheme<Theme>();
    const useModernTheme = shouldUseModernTheme(theme);
    const dir = useTextDirection();
    const isRtl = dir === "rtl";
    const classes = useStyles({ theme });
    const [hoveredChoiceIndex, setHoveredChoiceIndex] = React.useState<number | null>(null);
    const [draggingCard, setDraggingCard] = React.useState<VariableModel | null>(null);
    const [choiceTitleHeights, setChoiceTitleHeights] = React.useState<Record<string, number>>({});
    const [textCardHeights, setTextCardHeights] = React.useState<Record<string, number>>({});
    const [imageAspectRatioByCard, setImageAspectRatioByCard] = React.useState<
        Record<string, number>
    >({});
    const {
        ref: containerRef,
        dimensions: { innerWidth },
        calcDimensions,
    } = useElementDimensions<HTMLDivElement>(undefined, { sync: true, observeResize: true });
    const choices = sequencedVariables[0].choiceList;
    const setMentions = useSetVariableMentions();
    const cardVariableMentions = usePickMentions(question.variableNames);
    const getCardGroup = React.useCallback(
        (card: VariableModel) => translate(card, "group", language),
        [language],
    );
    const mentionValueForCard = React.useCallback(
        (card: VariableModel) => {
            const mentions = cardVariableMentions[card.name] || [];
            return mentions[0] && mentions[0].value;
        },
        [cardVariableMentions],
    );
    const unselectedCards = React.useMemo(
        () => sequencedVariables.filter((c) => mentionValueForCard(c) === undefined),
        [sequencedVariables, mentionValueForCard],
    );
    const groups = React.useMemo(
        () => uniq(sequencedVariables.map(getCardGroup)),
        [sequencedVariables, getCardGroup],
    );

    useValidation(validateSignal, useDiscreteVariadicValidator(question, sequencedVariables));

    const insertionOrderSort = (a: VariableModel, b: VariableModel) =>
        insertionOrder.indexOf(a.id) - insertionOrder.indexOf(b.id);

    const unselectedCardGroup = (group: string) =>
        unselectedCards.filter((v) => getCardGroup(v) === group).sort(insertionOrderSort);

    const cardsForChoice = (choice: ChoiceModel) =>
        sequencedVariables
            .filter((v) => mentionValueForCard(v) === choice.value)
            .sort(insertionOrderSort);

    const cardHasImage = React.useCallback(
        (card: VariableModel) => Boolean(translate(card, "imageUrl", language)),
        [language],
    );

    React.useEffect(() => {
        const cleaners: Array<() => void> = [];

        sequencedVariables.forEach((card) => {
            const src = translate(card, "imageUrl", language);
            if (!src) {
                return;
            }

            const img = new window.Image();
            const onLoad = () => {
                if (img.naturalWidth > 0 && img.naturalHeight > 0) {
                    setImageAspectRatioByCard((state) => ({
                        ...state,
                        [card.name]: img.naturalWidth / img.naturalHeight,
                    }));
                }
            };
            img.addEventListener("load", onLoad);
            img.src = src;
            cleaners.push(() => img.removeEventListener("load", onLoad));
        });

        return () => {
            cleaners.forEach((clean) => clean());
        };
    }, [language, sequencedVariables]);

    const getImageCardHeight = React.useCallback(
        (card: VariableModel, width: number) => {
            const aspectRatio = imageAspectRatioByCard[card.name];
            if (!aspectRatio) {
                return width;
            }
            return Math.ceil(width / aspectRatio);
        },
        [imageAspectRatioByCard],
    );

    const getMentions = (controlProps: ControlProps, variableName?: string) => {
        const [variable] = controlProps.question.variables;
        const mentions = controlProps.mentions[(variableName || variable.name).toUpperCase()] || [];
        return mentions.filter((mention) => mention.matrix === controlProps.matrix);
    };

    const getMention = (controlProps: ControlProps, variableName?: string, mentionIndex?: number) =>
        getMentions(controlProps, variableName).find((m) => m.index === (mentionIndex || 1));

    const getMentionValue = (
        controlProps: ControlProps,
        variableName?: string,
        mentionIndex?: number,
    ) => (getMention(controlProps, variableName, mentionIndex) || { value: "" }).value;

    const getCurrentChoice = (controlProps: ControlProps, variableName: VariableModel) => {
        const value = getMentionValue(controlProps, variableName.name);
        return getChoice(variableName, value);
    };

    const legacyLayout = React.useMemo(() => {
        if (useModernTheme) {
            return null;
        }

        const bucketPadding = innerWidth * PADDING_RATIO;
        const bucketMargin = innerWidth * MARGIN_RATIO;
        const cardSpacing = innerWidth * CARD_MAX_SPACING_RATIO;
        const fontSize = clamp(innerWidth * FONT_SIZE_RATIO, MIN_FONT_SIZE, MAX_FONT_SIZE);
        const lineHeight = fontSize * 3;
        const bucketWidth = innerWidth / 2 - bucketMargin / 2;
        const cardSize = clamp(
            (bucketWidth - bucketPadding * 2) * CARD_SIZE_RATIO,
            0,
            MAX_CARD_SIZE,
        );
        const cardPadding = cardSize * PADDING_RATIO;

        const getCardHeight = (card: VariableModel) =>
            cardHasImage(card) ? getImageCardHeight(card, cardSize) : cardSize;

        const maxCardHeight = Math.max(
            cardSize,
            ...sequencedVariables.map((card) => getCardHeight(card)),
        );

        const bucketHeight = lineHeight + maxCardHeight + bucketPadding;
        const bucketsHeight = Math.ceil(choices.length / 2) * (bucketHeight + bucketMargin);
        const groupsHeight =
            Math.max(Math.ceil(groups.length / 2), 1) * (bucketHeight + bucketMargin);
        const containerHeight = bucketsHeight + groupsHeight;

        const bounds = {
            top: -BOUNDS_MARGIN,
            bottom: containerHeight + BOUNDS_MARGIN,
            left: -BOUNDS_MARGIN,
            right: innerWidth + BOUNDS_MARGIN,
        };

        const cardTop = (card: VariableModel) => {
            const value = mentionValueForCard(card);
            const group = getCardGroup(card);
            const i = choices.findIndex((c) => c.value === value);
            if (i === -1) {
                const groupIndex = groups.indexOf(group);
                return (
                    bucketsHeight +
                    Math.floor(groupIndex / 2) * (bucketHeight + bucketMargin) +
                    lineHeight
                );
            }
            return Math.floor(i / 2) * (bucketHeight + bucketMargin) + lineHeight;
        };

        const cardLeft = (card: VariableModel) => {
            const value = mentionValueForCard(card);
            const choice = choices.find((c) => c.value === value);
            const group = getCardGroup(card);
            const cards = choice ? cardsForChoice(choice) : unselectedCardGroup(group);
            const spacing = clamp(
                (bucketWidth - bucketPadding - cardSize) / cards.length,
                0,
                cardSpacing,
            );
            const choiceIndex = choice ? choices.indexOf(choice) : groups.indexOf(group);
            const cardIndex = cards.indexOf(card);
            const offset = cardIndex * spacing;
            return (choiceIndex % 2) * (innerWidth / 2 + bucketMargin / 2) + offset + bucketPadding;
        };

        const onChoiceDrop =
            (card: VariableModel) =>
            ([x, y]: number[]) => {
                const height = getCardHeight(card);
                const cx = x + cardSize / 2;
                const cy = y + height / 2;
                const r = Math.floor(cy / bucketHeight);
                const c = cx < innerWidth / 2 ? 0 : 1;
                const i = c + r * 2;
                const dropBucket = i >= 0 && i < choices.length ? i : undefined;

                if (dropBucket !== undefined) {
                    const dropChoice = choices[dropBucket];
                    setMentions(card.name, (b) => b.set(dropChoice.value));
                } else {
                    setMentions(card.name, (b) => b.clear());
                }
                setInsertionOrder([...insertionOrder.filter((id) => id !== card.id), card.id]);
            };

        return {
            fontSize,
            containerHeight,
            bounds,
            bucketWidth,
            bucketHeight,
            bucketMargin,
            lineHeight,
            bucketsHeight,
            cardSize,
            cardPadding,
            cardTop,
            cardLeft,
            onChoiceDrop,
            getCardHeight,
        };
    }, [
        useModernTheme,
        innerWidth,
        choices,
        groups,
        insertionOrder,
        mentionValueForCard,
        getCardGroup,
        setMentions,
        sequencedVariables,
        cardHasImage,
        getImageCardHeight,
    ]);

    const modernLayout = React.useMemo(() => {
        if (!useModernTheme) {
            return null;
        }

        const columnGap = CardSortTokens.resolveCardSortColumnGap(theme);
        const cardGap = CardSortTokens.resolveCardSortCardGap(theme);
        const bucketPadding = CardSortTokens.resolveCardSortBucketPadding(theme);
        const headerGap = theme.CardSortHeaderGap;
        const groupSectionGap = CardSortTokens.resolveCardSortGroupSectionGap(theme);
        const dragLiftZ = CardSortTokens.resolveCardSortDragLiftZ(theme);
        const dragShadowColor = CardSortTokens.resolveCardSortDragShadowColor(theme);
        const useMobileLayout = choices.length > 1 && innerWidth > 0 && innerWidth <= MOBILE_WIDTH;
        const columnCount = useMobileLayout ? 2 : 1 + choices.length;
        const columnWidth = (innerWidth - (columnCount - 1) * columnGap) / columnCount;
        const leftColumnWidth = columnWidth;
        const rightColumnWidth = columnWidth;
        const bodyFontSize =
            parseInt(String(theme.TextFontSize ?? theme.BaseFontSize), 10) || theme.BaseFontSize;
        const textCardHeight = CardSortTokens.resolveCardSortCardHeight(theme, bodyFontSize);
        const cardWidthInBucket =
            (useMobileLayout ? rightColumnWidth : columnWidth) - bucketPadding * 2;
        const titleLineHeight = bodyFontSize * 1.25;
        const groupHeaderHeight = titleLineHeight;
        const getChoiceTitleHeight = (choice: ChoiceModel) =>
            choiceTitleHeights[choice.name] ?? titleLineHeight;
        const sourceColumnLeft = useMobileLayout
            ? isRtl
                ? rightColumnWidth + columnGap
                : 0
            : columnLeft(visualColumnIndex(0, columnCount, isRtl), columnWidth, columnGap);
        const choiceColumnLeft = (choiceIndex: number) =>
            useMobileLayout
                ? isRtl
                    ? 0
                    : leftColumnWidth + columnGap
                : columnLeft(
                      visualColumnIndex(choiceIndex + 1, columnCount, isRtl),
                      columnWidth,
                      columnGap,
                  );

        const getCardWidth = (card: VariableModel) => {
            const value = mentionValueForCard(card);
            const inChoice = choices.some((c) => c.value === value);
            if (inChoice) {
                return cardWidthInBucket;
            }
            return leftColumnWidth;
        };

        const getCardHeight = (card: VariableModel) =>
            cardHasImage(card)
                ? getImageCardHeight(card, getCardWidth(card))
                : Math.max(textCardHeights[card.name] ?? 0, textCardHeight);

        const stackHeight = (cards: VariableModel[]) =>
            cards.reduce(
                (sum, card, index) => sum + getCardHeight(card) + (index > 0 ? cardGap : 0),
                0,
            );

        const precedingCardsOffset = (cards: VariableModel[], cardIndex: number) =>
            cards.slice(0, cardIndex).reduce((sum, c) => sum + getCardHeight(c) + cardGap, 0);

        const groupSectionHeights = groups.map((group) => {
            const cards = unselectedCardGroup(group);
            return (group ? groupHeaderHeight + headerGap : 0) + stackHeight(cards);
        });

        const leftColumnHeight = groups.reduce((height, group, groupIndex) => {
            const sectionHeight = groupSectionHeights[groupIndex] ?? 0;
            const gap = groupIndex > 0 ? groupSectionGap : 0;
            return height + gap + sectionHeight;
        }, 0);

        const choiceContentHeight = (choice: ChoiceModel) => {
            const cards = cardsForChoice(choice);
            return (
                bucketPadding * 2 + getChoiceTitleHeight(choice) + headerGap + stackHeight(cards)
            );
        };

        const maxChoiceTitleHeight = Math.max(
            titleLineHeight,
            ...choices.map((choice) => getChoiceTitleHeight(choice)),
        );

        const emptyBucketMinHeight =
            bucketPadding * 2 + maxChoiceTitleHeight + headerGap + textCardHeight * 2 + cardGap;

        const groupSectionTops = groups.reduce<number[]>((tops, group, groupIndex) => {
            if (groupIndex === 0) {
                tops.push(0);
                return tops;
            }
            tops.push(
                tops[groupIndex - 1] + groupSectionGap + (groupSectionHeights[groupIndex - 1] ?? 0),
            );
            return tops;
        }, []);

        const choiceBucketLayouts: ChoiceBucketLayout[] = useMobileLayout
            ? choices.reduce<ChoiceBucketLayout[]>((layouts, choice, choiceIndex) => {
                  const contentHeight = choiceContentHeight(choice);
                  const alignedGroupHeight = groupSectionHeights[choiceIndex] ?? 0;
                  const height = Math.max(contentHeight, alignedGroupHeight, emptyBucketMinHeight);
                  const alignedTop =
                      choiceIndex < groups.length
                          ? (groupSectionTops[choiceIndex] ?? 0)
                          : layouts[choiceIndex - 1].top +
                            layouts[choiceIndex - 1].height +
                            groupSectionGap;
                  const minTop =
                      choiceIndex > 0
                          ? layouts[choiceIndex - 1].top +
                            layouts[choiceIndex - 1].height +
                            groupSectionGap
                          : 0;

                  layouts.push({
                      top: Math.max(alignedTop, minTop),
                      height,
                      left: choiceColumnLeft(choiceIndex),
                  });
                  return layouts;
              }, [])
            : [];

        const choiceColumnHeights = choices.map((choice) => choiceContentHeight(choice));

        const containerHeight = useMobileLayout
            ? Math.max(
                  leftColumnHeight,
                  emptyBucketMinHeight,
                  ...choiceBucketLayouts.map((layout) => layout.top + layout.height),
              )
            : Math.max(leftColumnHeight, emptyBucketMinHeight, ...choiceColumnHeights);

        const bounds = {
            top: -BOUNDS_MARGIN,
            bottom: containerHeight + BOUNDS_MARGIN,
            left: -BOUNDS_MARGIN,
            right: innerWidth + BOUNDS_MARGIN,
        };

        const cardTop = (card: VariableModel) => {
            const value = mentionValueForCard(card);
            const choice = choices.find((c) => c.value === value);
            if (choice) {
                const cards = cardsForChoice(choice);
                const cardIndex = cards.indexOf(card);
                const choiceIndex = choices.indexOf(choice);
                const bucketTop = useMobileLayout
                    ? (choiceBucketLayouts[choiceIndex]?.top ?? 0)
                    : 0;

                return (
                    bucketTop +
                    bucketPadding +
                    getChoiceTitleHeight(choice) +
                    headerGap +
                    precedingCardsOffset(cards, cardIndex)
                );
            }

            const group = getCardGroup(card);
            const groupIndex = groups.indexOf(group);
            const cards = unselectedCardGroup(group);
            const cardIndex = cards.indexOf(card);
            const sectionTop = groupSectionTops[groupIndex] ?? 0;
            const headerOffset = group ? groupHeaderHeight + headerGap : 0;
            return sectionTop + headerOffset + precedingCardsOffset(cards, cardIndex);
        };

        const cardLeft = (card: VariableModel) => {
            const value = mentionValueForCard(card);
            const choice = choices.find((c) => c.value === value);
            if (choice) {
                const choiceIndex = choices.indexOf(choice);
                if (useMobileLayout) {
                    return (
                        (choiceBucketLayouts[choiceIndex]?.left ?? choiceColumnLeft(choiceIndex)) +
                        bucketPadding
                    );
                }
                return choiceColumnLeft(choiceIndex) + bucketPadding;
            }

            return sourceColumnLeft;
        };

        const onChoiceDrop =
            (card: VariableModel) =>
            ([x, y]: number[]) => {
                const width = getCardWidth(card);
                const height = getCardHeight(card);
                const cx = x + width / 2;
                const cy = y + height / 2;

                if (useMobileLayout) {
                    const choiceIndex = choiceIndexAtMobile(
                        cx,
                        cy,
                        sourceColumnLeft,
                        leftColumnWidth,
                        choiceBucketLayouts,
                    );
                    if (choiceIndex >= 0) {
                        const dropChoice = choices[choiceIndex];
                        setMentions(card.name, (b) => b.set(dropChoice.value));
                    } else {
                        setMentions(card.name, (b) => b.clear());
                    }
                } else {
                    const visualCol = columnIndexAt(cx, innerWidth, columnCount, columnGap);
                    const logicalCol =
                        visualCol < 0
                            ? -1
                            : visualColumnIndex(visualCol, columnCount, isRtl);

                    if (logicalCol > 0 && logicalCol <= choices.length) {
                        const dropChoice = choices[logicalCol - 1];
                        setMentions(card.name, (b) => b.set(dropChoice.value));
                    } else {
                        setMentions(card.name, (b) => b.clear());
                    }
                }
                setInsertionOrder([...insertionOrder.filter((id) => id !== card.id), card.id]);
            };

        return {
            bodyFontSize,
            containerHeight,
            bounds,
            columnWidth,
            columnGap,
            textCardHeight,
            getCardHeight,
            getCardWidth,
            stackHeight,
            bucketPadding,
            headerGap,
            cardGap,
            titleLineHeight,
            getChoiceTitleHeight,
            groupHeaderHeight,
            groupSectionTops,
            dragLiftZ,
            dragShadowColor,
            cardTop,
            cardLeft,
            onChoiceDrop,
            useMobileLayout,
            leftColumnWidth,
            rightColumnWidth,
            choiceBucketLayouts,
            sourceColumnLeft,
            isRtl,
        };
    }, [
        useModernTheme,
        theme,
        innerWidth,
        choices,
        groups,
        insertionOrder,
        mentionValueForCard,
        getCardGroup,
        setMentions,
        sequencedVariables,
        cardHasImage,
        getImageCardHeight,
        choiceTitleHeights,
        textCardHeights,
        isRtl,
    ]);

    const layout = useModernTheme ? modernLayout : legacyLayout;
    const modernColumnCount = modernLayout?.useMobileLayout ? 2 : 1 + choices.length;

    React.useLayoutEffect(() => {
        calcDimensions();
    }, [
        calcDimensions,
        choices.length,
        groups.length,
        useModernTheme,
        sequencedVariables.length,
        imageAspectRatioByCard,
    ]);

    const onChoiceTitleHeight = React.useCallback((choiceName: string, height: number) => {
        setChoiceTitleHeights((prev) => {
            if (prev[choiceName] === height) {
                return prev;
            }
            return { ...prev, [choiceName]: height };
        });
    }, []);

    const onTextCardHeight = React.useCallback((cardName: string, height: number) => {
        if (height <= 0) {
            return;
        }
        setTextCardHeights((prev) => {
            if (prev[cardName] === height) {
                return prev;
            }
            return { ...prev, [cardName]: height };
        });
    }, []);

    const awaitingWidth = useModernTheme && innerWidth <= 0;

    const getDropHereStyle = React.useCallback(
        (choiceIndex: number) => {
            if (!useModernTheme || !modernLayout) {
                return undefined;
            }

            const choice = choices[choiceIndex];
            const cards = choice ? cardsForChoice(choice) : [];
            const bucketWidth = modernLayout.useMobileLayout
                ? modernLayout.rightColumnWidth
                : modernLayout.columnWidth;
            const dropCardHeight = draggingCard
                ? modernLayout.getCardHeight(draggingCard)
                : modernLayout.textCardHeight;

            return {
                left: modernLayout.bucketPadding,
                top:
                    modernLayout.bucketPadding +
                    (choice
                        ? modernLayout.getChoiceTitleHeight(choice)
                        : modernLayout.titleLineHeight) +
                    modernLayout.headerGap +
                    modernLayout.stackHeight(cards) +
                    (cards.length > 0 ? modernLayout.cardGap : 0),
                width: bucketWidth - modernLayout.bucketPadding * 2,
                height: dropCardHeight,
            };
        },
        [choices, cardsForChoice, draggingCard, modernLayout, useModernTheme],
    );

    const onCardHover = React.useCallback(
        (card: VariableModel) => (xy: number[] | null) => {
            if (!useModernTheme || !modernLayout) {
                setHoveredChoiceIndex(null);
                return;
            }

            if (xy == null) {
                setHoveredChoiceIndex(null);
                setDraggingCard(null);
                return;
            }

            setDraggingCard(card);

            const width = modernLayout.getCardWidth(card);
            const height = modernLayout.getCardHeight(card);
            const cx = xy[0] + width / 2;
            const cy = xy[1] + height / 2;

            if (modernLayout.useMobileLayout) {
                const choiceIndex = choiceIndexAtMobile(
                    cx,
                    cy,
                    modernLayout.sourceColumnLeft,
                    modernLayout.leftColumnWidth,
                    modernLayout.choiceBucketLayouts,
                );
                setHoveredChoiceIndex(choiceIndex >= 0 ? choiceIndex : null);
                return;
            }

            const visualCol = columnIndexAt(
                cx,
                innerWidth,
                modernColumnCount,
                modernLayout.columnGap,
            );
            const logicalCol =
                visualCol < 0
                    ? -1
                    : visualColumnIndex(visualCol, modernColumnCount, modernLayout.isRtl);

            if (logicalCol > 0 && logicalCol <= choices.length) {
                setHoveredChoiceIndex(logicalCol - 1);
            } else {
                setHoveredChoiceIndex(null);
            }
        },
        [
            choices.length,
            innerWidth,
            modernColumnCount,
            modernLayout,
            setHoveredChoiceIndex,
            useModernTheme,
        ],
    );

    if (!layout || awaitingWidth) {
        return (
            <div
                className={classes.container}
                ref={containerRef}
                style={awaitingWidth ? { minHeight: 1 } : undefined}
            />
        );
    }

    const hoveredDropHereExpansion =
        useModernTheme && modernLayout && hoveredChoiceIndex !== null
            ? (() => {
                  const choiceIndex = hoveredChoiceIndex;
                  const choice = choices[choiceIndex];
                  const cards = choice ? cardsForChoice(choice) : [];
                  const dropCardHeight = draggingCard
                      ? modernLayout.getCardHeight(draggingCard)
                      : modernLayout.textCardHeight;
                  const contentWithDropHere =
                      modernLayout.bucketPadding * 2 +
                      (choice
                          ? modernLayout.getChoiceTitleHeight(choice)
                          : modernLayout.titleLineHeight) +
                      modernLayout.headerGap +
                      modernLayout.stackHeight(cards) +
                      (cards.length > 0 ? modernLayout.cardGap : 0) +
                      dropCardHeight;
                  const bucketLayout = modernLayout.useMobileLayout
                      ? modernLayout.choiceBucketLayouts[choiceIndex]
                      : undefined;
                  const currentBucketHeight = bucketLayout?.height ?? modernLayout.containerHeight;

                  return Math.max(0, contentWithDropHere - currentBucketHeight);
              })()
            : 0;

    const effectiveContainerHeight = layout.containerHeight + hoveredDropHereExpansion;
    const dragBounds =
        hoveredDropHereExpansion > 0
            ? { ...layout.bounds, bottom: layout.bounds.bottom + hoveredDropHereExpansion }
            : layout.bounds;

    const containerFontSize = useModernTheme ? modernLayout!.bodyFontSize : legacyLayout!.fontSize;

    return (
        <div
            className={classes.container}
            ref={containerRef}
            style={{
                height: effectiveContainerHeight,
                fontSize: `${containerFontSize}px`,
            }}
        >
            {useModernTheme && modernLayout
                ? choices.map((c, i) => {
                      const bucketLayout = modernLayout.useMobileLayout
                          ? modernLayout.choiceBucketLayouts[i]
                          : undefined;
                      let bucketTop = bucketLayout?.top ?? 0;
                      if (
                          modernLayout.useMobileLayout &&
                          hoveredChoiceIndex !== null &&
                          i > hoveredChoiceIndex
                      ) {
                          bucketTop += hoveredDropHereExpansion;
                      }
                      const bucketHeight = modernLayout.useMobileLayout
                          ? (bucketLayout?.height ?? effectiveContainerHeight) +
                            (hoveredChoiceIndex === i ? hoveredDropHereExpansion : 0)
                          : effectiveContainerHeight;

                      return (
                          <CardSortBucket
                              id={c.elementId}
                              key={c.name}
                              modern
                              color={getSettingString(c, "Color")}
                              text={translate(c, "text", language)}
                              top={bucketTop}
                              left={
                                  bucketLayout?.left ??
                                  columnLeft(
                                      visualColumnIndex(
                                          i + 1,
                                          modernColumnCount,
                                          modernLayout.isRtl,
                                      ),
                                      modernLayout.columnWidth,
                                      modernLayout.columnGap,
                                  )
                              }
                              width={
                                  modernLayout.useMobileLayout
                                      ? modernLayout.rightColumnWidth
                                      : modernLayout.columnWidth
                              }
                              height={bucketHeight}
                              lineHeight={modernLayout.titleLineHeight}
                              showDropHere={hoveredChoiceIndex === i}
                              dropHereStyle={
                                  hoveredChoiceIndex === i ? getDropHereStyle(i) : undefined
                              }
                              titleKey={c.name}
                              onTitleHeight={onChoiceTitleHeight}
                          />
                      );
                  })
                : legacyLayout &&
                  sequencedVariables[0].choiceList.map((c, i) => (
                      <CardSortBucket
                          id={c.elementId}
                          key={c.name}
                          color={getSettingString(c, "Color")}
                          text={translate(c, "text", language)}
                          top={
                              Math.floor(i / 2) *
                              (legacyLayout.bucketHeight + legacyLayout.bucketMargin)
                          }
                          left={(i % 2) * (innerWidth / 2 + legacyLayout.bucketMargin / 2)}
                          width={legacyLayout.bucketWidth}
                          height={legacyLayout.bucketHeight}
                          lineHeight={legacyLayout.lineHeight}
                      />
                  ))}

            {!useModernTheme &&
                legacyLayout &&
                groups.map((g, i) => (
                    <CardSortGroup
                        key={g}
                        text={g}
                        top={
                            legacyLayout.bucketsHeight +
                            Math.floor(i / 2) *
                                (legacyLayout.bucketHeight + legacyLayout.bucketMargin)
                        }
                        left={(i % 2) * (innerWidth / 2 + legacyLayout.bucketMargin / 2)}
                        width={legacyLayout.bucketWidth}
                        height={legacyLayout.bucketHeight}
                        lineHeight={legacyLayout.lineHeight}
                    />
                ))}

            {useModernTheme &&
                modernLayout &&
                groups.map((g, i) => (
                    <CardSortGroupHeader
                        key={`header-${g || i}`}
                        text={g}
                        top={modernLayout.groupSectionTops[i]}
                        left={modernLayout.sourceColumnLeft}
                        width={
                            modernLayout.useMobileLayout
                                ? modernLayout.leftColumnWidth
                                : modernLayout.columnWidth
                        }
                        lineHeight={modernLayout.groupHeaderHeight}
                    />
                ))}

            {sequencedVariables.sort(insertionOrderSort).map((v) => {
                const choice = getCurrentChoice(props, v);

                return (
                    <CardSortCard
                        id={v.elementId}
                        key={v.name}
                        modern={useModernTheme}
                        color={getSettingString(v, "Color")}
                        text={translate(v, "text", language)}
                        imageUrl={translate(v, "imageUrl", language)}
                        onDrop={layout.onChoiceDrop(v)}
                        onHover={useModernTheme ? onCardHover(v) : undefined}
                        top={layout.cardTop(v)}
                        left={layout.cardLeft(v)}
                        width={
                            useModernTheme ? modernLayout!.getCardWidth(v) : legacyLayout!.cardSize
                        }
                        height={
                            useModernTheme
                                ? modernLayout!.getCardHeight(v)
                                : legacyLayout!.getCardHeight(v)
                        }
                        padding={useModernTheme ? 0 : legacyLayout!.cardPadding}
                        bounds={dragBounds}
                        areaCode={choice !== undefined ? choice.value : "none"}
                        dragLiftZ={useModernTheme ? modernLayout!.dragLiftZ : undefined}
                        dragShadowColor={useModernTheme ? modernLayout!.dragShadowColor : undefined}
                        cardKey={v.name}
                        onTextHeight={useModernTheme ? onTextCardHeight : undefined}
                    />
                );
            })}
        </div>
    );
}
